import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { PlayCircle } from "lucide-react";

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  console.log(location.state)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Reset previous error
    setEmailError("");

    const emailPattern = /^[^\s@]+@[^\s@]+\.com$/;
    if (!emailPattern.test(email)) {
      setEmailError("Please enter a valid email format");
      setIsLoading(false);
      return;
    }

    try {
      const { success, message } = await login(email, password);

      if (success) {
        toast({
          title: "Selamat Datang!",
          description: "Berhasil masuk login.",
        });

        const redirectTo = location.state?.from || "/";

        if (redirectTo != "/") {
          if (location.state?.from === "videos") {
            navigate(location.state.path);
          }
          else {
            navigate(redirectTo, { replace: true });
          }
        } else {
          navigate("/landing");
        }

      } else {
        toast({
          title: "Login gagal",
          description: message,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Terjadi kesalahan. Mohon coba lagi.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4">
      {/* BACKGROUND */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-600 via-emerald-500 to-lime-400" />
      <div className="absolute inset-0 bg-black/30" />

      {/* CONTENT */}
      <div className="relative w-full max-w-md">
        {/* BRAND */}
        <div className="text-center mb-10">
          <div
            className="flex items-center justify-center gap-3 mb-3 cursor-pointer hover:opacity-80 transition"
            onClick={() => navigate("/landing")}
          >
            <PlayCircle className="w-10 h-10 text-white drop-shadow" />
            <h1 className="text-3xl font-bold text-white tracking-tight">
              Court Replay
            </h1>
          </div>
          <p className="text-white/80 text-sm">
            Relive every moment of your game
          </p>
        </div>

        {/* CARD */}
        <Card className="border-0 bg-white/90 backdrop-blur-xl shadow-2xl rounded-2xl">
          <CardHeader className="text-center space-y-1">
            <CardTitle className="text-2xl">Welcome Back</CardTitle>
            <CardDescription>
              Sign in to continue watching your replays
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* EMAIL */}
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 rounded-xl"
                  required
                />
                {emailError && (
                  <p className="text-sm text-red-500">{emailError}</p>
                )}
              </div>

              {/* PASSWORD */}
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 rounded-xl"
                  required
                />
              </div>

              {/* SUBMIT */}
              <Button
                type="submit"
                className="w-full h-11 rounded-xl text-base"
                disabled={isLoading}
              >
                {isLoading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            {/* FOOTER */}
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">
                Don&apos;t have an account?
              </span>{" "}
              <Link
                to="/register"
                state={location.state}
                className="font-medium text-primary hover:underline"
              >
                Sign up
              </Link>
            </div>
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">
                Forgot your password ? Reset your password
              </span>{" "}
              <Link
                to="/reset-password"
                className="font-medium text-primary hover:underline"
              >
                Here
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
